import { ApolloError } from '@apollo/client';

import { IPaginationProps, EmptyValue, TableField, DataTable, Row } from '@occc/ui-design-system';
import { readableDate } from '@occc-app/lib/format';
import { MembershipActivityFragment } from '@occc/graphql';

import {
  getLocation,
  getPointChangeAmount,
  getTotalAmount,
  getTotalGallons,
} from './membership-activity.utils';
import { MembershipActivityLocation } from './components/membership-activity-location';
import { MembershipActivityTransactionAmount } from './components/membership-activity-amount';
import { MembershipActivityPointChange } from './components/membership-activity-point-change';
import { MembershipActivitySource } from './components/membership-activity-source';

type MembershipActivityProps = {
  loading: boolean;
  activities?: MembershipActivityFragment[] | null;
  error?: ApolloError;
  paginationProps: IPaginationProps;
  onRowItemClick: (activity: MembershipActivityFragment) => void;
};

/**
 * Convert a MembershipActivityFragment into its corresponding table
 * fields
 */
const makeFields: (activity: MembershipActivityFragment) => TableField[] = (activity) => {
  const location = getLocation(activity);
  const totalAmount = getTotalAmount(activity);

  return [
    {
      label: 'Date',
      value: readableDate(activity.createdAt),
    },
    { label: 'Source', value: <MembershipActivitySource activity={activity} /> },
    {
      label: 'Point Change',
      value: <MembershipActivityPointChange amount={getPointChangeAmount(activity)} />,
    },
    {
      label: 'Location',
      value: location ? (
        <MembershipActivityLocation
          name={location.name ?? undefined}
          address1={location.address1 ?? undefined}
          city={location.city ?? undefined}
          state={location.state ?? undefined}
        />
      ) : (
        <EmptyValue />
      ),
    },
    {
      label: 'Total Gallons',
      value: getTotalGallons(activity) ?? <EmptyValue />,
    },
    {
      label: 'Total Amount',
      value: <MembershipActivityTransactionAmount amount={totalAmount || 0} />,
    },
  ];
};

const headers = [
  { label: 'Date' },
  { label: 'Source' },
  { label: 'Point Change' },
  { label: 'Location' },
  { label: 'Total Gallons' },
  { label: 'Total Amount' },
];

export const MembershipActivity = (props: MembershipActivityProps) => {
  const { activities, loading, error, paginationProps, onRowItemClick } = props;
  const rows: Row[] | null | undefined = activities?.map((activity) => {
    const fields = makeFields(activity);

    return {
      key: activity.id,
      fields,
      onClick: () => {
        onRowItemClick(activity);
      },
    };
  });

  return (
    <DataTable
      headers={headers}
      rows={rows}
      loading={loading}
      error={error}
      paginationProps={paginationProps}
    />
  );
};
